import React, { Dispatch, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';
import { IAppReduxStore } from '../../../redux/store';
import {
    createActionColDef,
    createActionsColumn,
    getColumns,
    getSelectedDocIds,
    processDocuments,
} from '../DocTable/DocTableUtils';
import StyledDataGrid from '../DocTable/Styled/StyledDataGridPro';
import { useTranslation } from 'react-i18next';
import {
    GridPreferencePanelsValue,
    GridRowParams,
    GridRowSelectionModel,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import { updateSelectedDocuments } from '../../../redux/project/documents/sliceProjectSelectedDocuments';
import { useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import DocTableError from '../DocTable/DocTableError';
import DocTableAdditionalDocs from '../DocTable/DocTableAdditionalDocs';
import ModalResultTableColumnMenu from './ModalResultTableColumnMenu';
import { getMultilineFields } from '../../../util/DataUtils';
import DocTableToolbar from '../DocTable/DocTableToolbar';
import { setHasFilteredItems, setShowFilter } from '../../../redux/user/sliceUserSettings';
declare module '@mui/x-data-grid-pro' {
    interface GridColumnMenuProps {
        tableFor?: 'searchResults' | 'selectedDocuments';
    }
}

/**
 * DataGrid component that is used inside the SelectedDocumentsView.
 */
export default ({ documents, checkboxSelection = false, tableFor }: IModalResultTableProps) => {
    const dispatch: Dispatch<unknown> = useDispatch();
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** Get the replicaId, areaName, viewName and category from the react router. */
    const { replicaId, areaName } = useParams();

    /** Get relevant values from Redux store. */
    const fieldSchema = useSelector((store: IAppReduxStore) => store.projectViewData.schema.data);
    const fields = useSelector((store: IAppReduxStore) => store.projectViewData.fields.data);
    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);
    const {
        loading: isAdvancedSearchResultsLoading,
        error: isAdvancedSearchResultsError,
        areaSearchText,
    } = useSelector((store: IAppReduxStore) => store.projectAdvancedSearch);
    const { storage: selectedDocuments } = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments,
    );
    const { showFilter } = useSelector((store: IAppReduxStore) => store.userSettings);

    const gridApiRef = useGridApiRef();

    /** Create the row objects from the input documents. */
    const rows = processDocuments(documents, fields);

    const columns = useMemo(() => {
        return [
            tableFor === 'searchResults'
                ? createActionColDef(fieldSchema, docTypes, t)
                : createActionsColumn(),
            ...getColumns(
                fieldSchema,
                fields,
                rows,
                t,
                false,
                tableFor === 'searchResults' ? areaSearchText : '',
            ).filter(Boolean),
        ];
    }, [tableFor, areaSearchText, fieldSchema, docTypes, rows, i18n.language]);

    useEffect(() => {
        dispatch(setShowFilter(false));
        dispatch(setHasFilteredItems(false));
    }, []);

    useEffect(() => {
        showFilter
            ? gridApiRef.current?.showPreferences(GridPreferencePanelsValue.filters)
            : gridApiRef.current?.hidePreferences();
    }, [showFilter]);

    /** Add component keyboard events. */
    useEffect(() => {
        if (!checkboxSelection) return;
        window.addEventListener('keydown', handleKeydown);

        /** Remove the listener when the component is no longer needed. */
        return () => {
            window.removeEventListener('keydown', handleKeydown);
        };
    }, [checkboxSelection]);

    /**
     * Updates the collection of selected documents.
     * @param rowSelectionModel -> Array of selected document ids from the DataGrid.
     */
    const setSelectionModel = useCallback(
        (rowSelectionModel: GridRowSelectionModel) => {
            const documentIds = getSelectedDocIds(rows, selectedDocuments, rowSelectionModel);
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds,
                }),
            );
        },
        [rows, selectedDocuments],
    );

    /**
     * Overrides CTRL+A sequence and Escape key to manipulate data grid row selection.
     * @param e - Keyboard event.
     */
    const handleKeydown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSelectionModel([]);

                return;
            }

            if (/a/i.test(e.key) && e.ctrlKey && documents?.length) {
                e.preventDefault();
                e.stopPropagation();

                setSelectionModel(documents.map(x => x.NoteId));
            }
        },
        [documents],
    );

    /**
     * Creates the Master Detail area for a given DataGrid row.
     * @param row - Target DataGrid row.
     */
    const getAdditionalDocuments = useCallback(
        ({ row }: GridRowParams) => {
            return <DocTableAdditionalDocs row={row} />;
        },
        [columns],
    );

    const fieldsWithMultilineType = useMemo(() => getMultilineFields(fields), [fields]);

    if (tableFor === 'searchResults' && isAdvancedSearchResultsError) {
        return <DocTableError />;
    }

    return (
        <ErrorBoundary fallback={<DocTableError />}>
            <StyledDataGrid
                apiRef={gridApiRef}
                checkboxSelection={checkboxSelection}
                columns={columns}
                rows={rows}
                slots={{
                    columnMenu: ModalResultTableColumnMenu,
                    toolbar: tableFor === 'searchResults' ? DocTableToolbar : null,
                }}
                slotProps={{
                    columnMenu: {
                        tableFor,
                    },
                    toolbar: {
                        tableFor,
                    },
                }}
                sortingOrder={['asc', 'desc']}
                /** Update the selection model when the user selects or de-selects a row. */
                onRowSelectionModelChange={setSelectionModel}
                /** Make DataGrid selection model controlled. */
                /** Populate the row's Master Detail section (if document has additional documents). */
                getDetailPanelContent={
                    tableFor === 'searchResults' && columns.length ? getAdditionalDocuments : null
                }
                rowSelectionModel={
                    tableFor === 'searchResults'
                        ? rows
                              .filter(
                                  x =>
                                      (selectedDocuments?.documentIds ?? []).indexOf(
                                          x.UniversalID,
                                      ) >= 0,
                              )
                              .map(x => x.NoteId)
                        : []
                }
                fieldsWithMultilineType={fieldsWithMultilineType}
                loading={tableFor === 'searchResults' && isAdvancedSearchResultsLoading}
                onPreferencePanelClose={({ openedPanelValue }) => {
                    openedPanelValue === 'filters' && dispatch(setShowFilter(false));
                }}
                onFilterModelChange={newFilterModel => {
                    dispatch(setHasFilteredItems(newFilterModel.items.some(item => item.value)));
                }}
            />
        </ErrorBoundary>
    );
};

/**
 * Interface that describes the props for the ModalResultTable component.
 */
export interface IModalResultTableProps {
    /** Array of documents to show. */
    documents: IDocDefaultFields[];
    checkboxSelection?: boolean;
    tableFor?: 'searchResults' | 'selectedDocuments';
}
